import React, { useCallback } from "react";
import styles from "./ActionsMenu.module.scss";
import classnames from "classnames";
import { SlOptionsVertical } from "react-icons/sl";
import { Popover } from "@whatslly/ui-components/Popover";
import { Svg } from "@whatslly/ui-components/v2/BaseElements/Svg";
import type { TuvisIconNamesType } from "@whatslly/types/UI-V2/BaseElements/Svg/TuvisIconNamesType";
import type { FloatingPlacement } from "../utils/floatingUtils";
export interface ActionType<T extends Record<string, any> = any> {
  children: React.ReactNode;
  icon: TuvisIconNamesType | React.ReactNode;
  onClick?: (item?: T) => void;
  href?: string;
  className?: string;
  testId?: string;
  hrefTarget?: string;
}
export const ActionsMenu: React.FC<{
  actions: ActionType[];
  testId?: string;
  className?: string;
  menuClassName?: string;
  item?: Record<string, any>;
  icon?: React.ReactNode;
  disabled?: boolean;
  position?: FloatingPlacement;
}> = ({ actions, testId, className, menuClassName, item, icon, disabled, position }) => {
  const handleTriggerClick = useCallback(
    (togglePopover: () => void) => (event: React.MouseEvent) => {
      if (disabled) {
        return;
      }

      event.preventDefault();
      event.stopPropagation();
      togglePopover();
    },
    [disabled]
  );

  return (
    <Popover
      placement={position ?? "bottom"}
      offset={6}
      contentClassName={classnames(styles.actionsMenu, menuClassName)}
      data-testid="actions-menu-floating-actions-container"
      content={({ closePopover }) => actions.map((action, i) => <Action {...action} item={item} hideActionsMenu={closePopover} key={i} />)}
    >
      {({ isOpen, triggerRef, togglePopover }) => (
        <div
          ref={(element) => {
            triggerRef.current = element;
          }}
          className={classnames(styles.actionsMenuContainer, className, {
            [styles.disabled]: disabled,
            [styles.taskActionsIsOpen]: isOpen,
          })}
          data-testid={testId}
          onClick={handleTriggerClick(togglePopover)}
        >
          {icon ? icon : <SlOptionsVertical />}
        </div>
      )}
    </Popover>
  );
};

const Action: React.FC<ActionType & { item?: unknown; hideActionsMenu: () => void }> = ({
  children,
  className,
  icon,
  onClick,
  href,
  testId,
  hrefTarget,
  item,
  hideActionsMenu,
}) => {
  const ActionContainer: React.FC<{ children?: React.ReactNode }> = useCallback(
    ({ children }) => {
      if (href) {
        return (
          <a
            className={classnames(styles.actionContainer, className)}
            onClick={(e) => {
              e.stopPropagation();
              hideActionsMenu();
              onClick?.(item);
            }}
            href={href}
            data-testid={testId}
            target={hrefTarget || "_blank"}
          >
            {children}
          </a>
        );
      }

      return (
        <div
          className={classnames(styles.actionContainer, className)}
          data-testid={testId}
          onClick={(e) => {
            e.stopPropagation();
            hideActionsMenu();
            onClick?.(item);
          }}
        >
          {children}
        </div>
      );
    },
    [href, className, testId, hrefTarget, hideActionsMenu, onClick, item]
  );
  return (
    <ActionContainer>
      {icon && <div className={styles.actionIconContainer}>{typeof icon === "string" ? <Svg iconName={icon as TuvisIconNamesType} /> : icon}</div>}
      {children}
    </ActionContainer>
  );
};
